import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComingSoonPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.remove();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/coming-soon/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubscribed(true);
        toast.success("Du bist auf der Liste!");
      } else {
        toast.error(data.detail || "Ein Fehler ist aufgetreten.");
      }
    } catch {
      toast.error("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="coming-soon-page">

      {/* Header */}
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between" data-testid="coming-soon-header">
        <div className="flex items-center gap-2.5">
          <img src="/logo.svg" alt="WebGenerator" className="w-8 h-8" />
          <span className="font-semibold text-lg tracking-tight text-slate-900">
            Web<span className="text-indigo-600">Generator</span>
          </span>
        </div>
        <button
          onClick={() => navigate("/anmelden?mode=login")}
          className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="coming-soon-login-btn"
        >
          Anmelden
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">

          {/* Status pill */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium mb-8"
            data-testid="coming-soon-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            In Entwicklung
          </div>

          {/* Headline */}
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight mb-4"
            data-testid="coming-soon-headline"
          >
            Wir starten bald.
          </h1>

          {/* Subtext */}
          <p
            className="text-slate-500 text-base leading-relaxed mb-10"
            data-testid="coming-soon-subheadline"
          >
            Professionelle Webseiten, vollautomatisch erstellt durch KI.
            <br className="hidden sm:block" />
            Trage dich ein und erfahre als Erster vom Launch.
          </p>

          {/* Form or Success */}
          {!subscribed ? (
            <form onSubmit={handleSubmit} className="mb-6" data-testid="coming-soon-form">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  className="flex-1 h-11 px-4 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-shadow"
                  data-testid="coming-soon-email-input"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="h-11 px-5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50 flex-shrink-0"
                  data-testid="coming-soon-submit-btn"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Eintragen
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                Kein Spam. Nur eine Nachricht zum Launch.
              </p>
            </form>
          ) : (
            <div className="mb-6 flex items-center justify-center gap-2 text-emerald-600 font-medium text-sm" data-testid="coming-soon-success">
              <Check className="w-4 h-4" />
              Du wirst benachrichtigt.
            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400" data-testid="coming-soon-footer">
        <p>&copy; 2026 WebGenerator</p>
        <div className="flex items-center gap-5">
          <button onClick={() => navigate("/impressum")} className="hover:text-slate-600 transition-colors" data-testid="coming-soon-impressum">Impressum</button>
          <button onClick={() => navigate("/datenschutz")} className="hover:text-slate-600 transition-colors" data-testid="coming-soon-datenschutz">Datenschutz</button>
          <a href="mailto:support@webgenerator.de" className="hover:text-slate-600 transition-colors" data-testid="coming-soon-email">Kontakt</a>
        </div>
      </footer>
    </div>
  );
}
